import Image from "next/image";
import classNames from "classnames";
import NextLink from "next/link";
import { Canvas, Typography, Link } from "@cappelendamm/design-system/react";
import { BookStencil } from "@cappelendamm/design-system/react";
import { BookItem } from "../types";
import { shouldShowAddToCartButton } from "@/products/selectors/productPurchaseSelectors";
import { getAddToCartButtonState } from "@/utils/getAddToCartButtonState";
import { ListingTags } from "@/search/components/item-listings/ListingTags";
import { getPageLink } from "@/navigation/selectors";
import { ListingItemProps } from "./types";
import { selectCollection } from "./selectors";
import { AddToCartButton } from "@/cart/components/AddToCartButton";
import { Cart, Clock } from "iconoir-react";
import { PricelistPrice } from "@/products/components/PricelistPrice";
import { MemberPrice } from "@/products/components/MemberPrice";

export const PRODUCT_ARTICLE_IMAGE_WIDTH = 140 as const;
export const PRODUCT_ARTICLE_IMAGE_HEIGHT = 222 as const;

export function BookListingItem({ item, config }: ListingItemProps<BookItem>) {
  const {
    objectID: sku,
    imageUrl,
    price,
    subjectToVAT,
    priceLists,
    itemDetails: { binding: format, title, language, slug, contributorByLine },
  } = item;

  const href = slug
    ? getPageLink({
        pageType: "book",
        params: slug,
      })
    : null;
  const collection = selectCollection(item);
  const bindings = [format].filter((binding): binding is string =>
    Boolean(binding),
  );
  const addToCartState = getAddToCartButtonState(
    item.canBeSold === true,
    item.statusMessage ?? undefined,
    item.publicationDate ?? undefined,
  );
  const showAddToCart =
    (config?.fields?.addToCart ?? false) &&
    shouldShowAddToCartButton(item) &&
    !addToCartState.disabled;

  return (
    <article
      className={classNames(
        "relative row-span-[var(--list-row-count)] grid min-w-0 grid-rows-subgrid gap-1",
        { "cursor-pointer": href },
        href
          ? "active:rounded active:outline-3 active:outline-offset-4 active:outline-[var(--cvr-canvas-background)]"
          : "",
      )}
    >
      <NextLink
        href={href ?? ""}
        aria-labelledby={`item-${sku}`}
        className="group relative row-start-1 -row-end-3 flex min-w-0 flex-col gap-1 no-underline"
      >
        <Canvas
          className="mb-1 px-2 py-2 md:px-6 md:py-2"
          style={{ height: PRODUCT_ARTICLE_IMAGE_HEIGHT + 16 }}
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={`Omslag av "${title}"`}
              width={PRODUCT_ARTICLE_IMAGE_WIDTH}
              height={PRODUCT_ARTICLE_IMAGE_HEIGHT}
              className="h-full w-auto object-contain transition-all sm:group-hover:scale-102 sm:group-hover:shadow-lg"
              quality={85}
            />
          ) : (
            <BookStencil
              style={
                {
                  "--book-width": `${PRODUCT_ARTICLE_IMAGE_WIDTH}px`,
                } as React.CSSProperties
              }
            />
          )}
        </Canvas>
        <div className="flex flex-col gap-1">
          <div className="empty:hidden">
            <ListingTags
              item={item}
              config={{
                showHighlighted: config?.tags?.highlighted ?? false,
                showInformational: config?.tags?.informational ?? false,
                showPromoted: config?.tags?.promoted ?? false,
                showPublicationDate: config?.tags?.publicationDate ?? false,
                showMemberPrice: config?.fields?.price ?? false,
              }}
            />
          </div>
          <Typography
            id={`item-${sku}`}
            as="h3"
            variant="title-md"
            className={classNames("line-clamp-2", {
              "sm:group-hover:underline": href,
            })}
          >
            {title}
          </Typography>
        </div>

        <Typography
          as="p"
          variant="body-base"
          className="mt-0 block truncate"
          muted
        >
          {contributorByLine}
        </Typography>
        {config?.fields?.series && collection ? (
          <div className="truncate text-sm">
            {collection.prefix}
            {collection.href ? (
              <Link
                as={NextLink}
                href={collection.href}
                variant="inline"
                className={classNames({
                  "inline p-0 font-semibold": collection.prefix === "En del av",
                })}
              >
                {collection.linkName}
              </Link>
            ) : (
              collection.linkName
            )}
          </div>
        ) : null}
      </NextLink>

      {config?.fields?.formatMeta ? (
        <div className="-row-end-2 mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {bindings.map((binding, index) => (
            <span
              key={binding}
              className="inline-flex items-center gap-2 whitespace-nowrap"
            >
              {index > 0 ? <span aria-hidden>·</span> : null}
              <span className="font-semibold first-letter:capitalize">
                {binding}
              </span>
            </span>
          ))}
          {language ? (
            <span className="inline-flex items-center gap-2 whitespace-nowrap">
              {bindings.length > 0 ? <span aria-hidden>·</span> : null}
              <span>{language}</span>
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="relative z-10 -row-start-1">
        <div className="flex flex-wrap items-center gap-2">
          {config?.fields?.price ? (
            <div className="flex min-w-0 flex-col gap-1 text-xl font-semibold [&_span]:text-left">
              <MemberPrice
                originalItemPrice={price}
                subjectToVAT={subjectToVAT}
                pricelists={priceLists}
              >
                <PricelistPrice
                  originalItemPrice={price}
                  subjectToVAT={subjectToVAT}
                  pricelists={priceLists}
                  variant="title-md"
                  className="!flex-col !items-start !gap-0.5 [&>div]:flex-col [&>div]:items-start [&>div]:gap-0.5 [&>div>span.order-last]:order-last"
                />
              </MemberPrice>
            </div>
          ) : null}
          {showAddToCart ? (
            <AddToCartButton
              sku={sku}
              canBeSold={item.canBeSold}
              statusMessage={item.statusMessage ?? undefined}
              publicationDate={item.publicationDate ?? undefined}
              className="!inline-flex !min-h-0 !w-auto !min-w-0 shrink-0 !px-3 !py-1.5 !text-sm [&>span]:gap-2"
              aria-label={addToCartState.label ?? "Legg i handlekurv"}
              title={addToCartState.label ?? "Legg i handlekurv"}
              preorder={{
                "aria-label": "Forhåndsbestill",
                title: "Forhåndsbestill",
                children: (
                  <>
                    <Clock
                      width={14}
                      height={14}
                      className="cvr-icon--stroke"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                    />
                    Forhåndsbestill
                  </>
                ),
              }}
            >
              <Cart
                width={16}
                height={16}
                className="cvr-icon--stroke ![stroke-width:2]"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              />
              Kjøp
            </AddToCartButton>
          ) : null}
        </div>
      </div>
    </article>
  );
}
